import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import * as turf from '@turf/turf';
import geojson2svg from 'geojson-to-svg';
import { startCase } from 'lodash';
import { firstValueFrom, Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { v4 as uuidv4 } from 'uuid';

import { environment } from '@env/environment';
import { Parcel, Stand } from '@shared/models';
import { deserialize } from '@shared/models/base-helper';
import { Base } from '@shared/models/base.model';
import { SpinnerService } from '@shared/spinner/spinner.service';
import { FormatHelperService } from '../helper/format-helper.service';

export interface CustomAreaValuationStandReq {
  geometry: string;
  site_id: number;
  current_stands: Array<Stand>;
}

export interface CustomStand {
  id: number | string;
  type: 'Feature';
  geometry: {
    type: string;
    coordinates: Array<any>;
  };
  properties: any;
}

export interface CustomAreaValuationStandArrayBackendResp {
  results: Array<CustomStand>;
  error: string;
}

export interface CustomAreaValuationStandArrayServiceResp {
  results: Array<Stand>;
  error: string;
}

export interface ParcelNumberList {
  gid: number;
  parcelnumb: string;
  center: {
    type: string;
    coordinates: [number, number];
  };
}

export interface ParcelNumberListDeSerialized {
  id: number;
  parcelNumber: string;
  county: string;
  state: string;
  coordinates: any;
}

export class CountyInfo extends Base {
  county: string;
  gid: number;
  state: string;
  averageErt: string;
  available: boolean;
  lastImport: string;
  regridFile: string;
  regridRefresh: string;
  status: string;
  upToDate: boolean;
}

interface QueryResponse {
  results: CountyInfo[];
  total: number;
}

@Injectable({
  providedIn: 'root',
})
export class GisService {
  apiUrl = environment.apiUrl + '/gis/';

  constructor(
    readonly http: HttpClient,
    readonly spinnerService: SpinnerService,
    public formatHelper: FormatHelperService
  ) {}


  getParcelListLikeParcelNumberAsync(
    parcelNumber: string, limit: number): Promise<ParcelNumberListDeSerialized[]> {
    let filters = new HttpParams();

    filters = filters.append('parcel_number', parcelNumber);
    if (limit) {
      filters = filters.append('limit', limit.toString());
    }

    const response$ = this.http
      .get<Array<ParcelNumberList>>(`${this.apiUrl}get_parcel_list_like_parcel_number`, { params: filters })
      .pipe(
        map((response: Array<ParcelNumberList>) =>
          response.map((parcelItem: any) => {
            return {
              id: parcelItem.gid,
              parcelNumber: parcelItem.parcelnumb,
              coordinates: [parcelItem.center.coordinates[0], parcelItem.center.coordinates[1]],
              county: startCase(parcelItem.county),
              state: parcelItem.state.toUpperCase(),
            };
          })
        )
      );

    return firstValueFrom(response$);
  }

  getParcelListLikeParcelNumber(
    parcelNumber: string, limit: number): Observable<Array<ParcelNumberListDeSerialized>> {
    let filters = new HttpParams();

    filters = filters.append('parcel_number', parcelNumber);
    if (limit) {
      filters = filters.append('limit', limit.toString());
    }

    return this.http
      .get<Array<ParcelNumberList>>(`${this.apiUrl}get_parcel_list_like_parcel_number`, { params: filters })
      .pipe(
        map((response: Array<ParcelNumberList>) =>
          response.map((parcelItem: any) => {
            return {
              id: parcelItem.gid,
              parcelNumber: parcelItem.parcelnumb,
              coordinates: [parcelItem.center.coordinates[0], parcelItem.center.coordinates[1]],
              county: startCase(parcelItem.county),
              state: parcelItem.state.toUpperCase()
            };
          })
        )
      );
  }

  getCountyFilteredList(county: string, limit: number): Observable<Array<CountyInfo>> {
    let filters = new HttpParams();

    filters = filters.append('county', county);
    if (limit) {
      filters = filters.append('limit', limit.toString());
    }

    return this.http
      .get<Array<CountyInfo>>(`${this.apiUrl}get_county_filtered_list`, {
        params: filters,
        headers: new HttpHeaders().set('X-FC-Noauth', 'true')
      })
      .pipe(map((response: Array<CountyInfo>) => deserialize(response, CountyInfo)));
  }

  getFilteredCountyData(filterString: string): Observable<QueryResponse> {
    return this.http.get<QueryResponse>(`${this.apiUrl}get_county_data${filterString}`).pipe(
      map(response => {
        const deserializedResponse: QueryResponse = {
          results: [],
          total: response.total,
        };
        for (const serializedProgram of response.results) {
          deserializedResponse.results.push(deserialize(serializedProgram, CountyInfo));
        }
        return deserializedResponse;
      })
    );
  }

  getOverlappingStands(siteId: number) {
    let filters = new HttpParams();
    filters = filters.append('site_id', siteId.toString());
    return firstValueFrom(
      this.http.get<string[]>(`${this.apiUrl}get_stand_overlap`, { params: filters })
    );
  }

  getReflagOverlapByClickedPoint(
    siteId: number,
    publicLandLayer: string,
    geometry: any,
    lng: number = null,
    lat: number = null,
  ) {
    const body = {
      lng: lng?.toString(),
      lat: lat?.toString(),
      geometry: JSON.stringify(geometry),
      site_id: siteId,
      public_land_layer: publicLandLayer,
    };
    return this.http.post<any>(`${this.apiUrl}get_redflag_overlap`, body)
      .pipe(
        map((response: CustomAreaValuationStandArrayBackendResp) => {
          let standList = [];
          if (response.results) {
            standList = response.results.map(s => this.constructStand(s));
          }
          return {
            results: standList,
            error: response.error,
          };
        }),
        catchError(error => {
          return of(error);
        })
      );
  }

  getCustomAreaValuationOfStand(
    geometry: any,
    siteId: number,
    currentStands: Stand[] = []
  ): Observable<CustomAreaValuationStandArrayServiceResp> {
    let paramStand = [];
    currentStands.forEach(stand => {
      let clonedStand = new Stand();
      clonedStand.customId = stand.customId;
      clonedStand.area = stand.area;
      clonedStand.valuation = stand.valuation.toString().split(',')[0];
      paramStand.push(clonedStand);
    });
    const body: CustomAreaValuationStandReq = {
      geometry: JSON.stringify(geometry),
      site_id: siteId,
      current_stands: paramStand,
    };

    return this.http
      .post<CustomAreaValuationStandArrayBackendResp>(`${this.apiUrl}get_custom_polygon_valuation`, body)
      .pipe(
        map((response: CustomAreaValuationStandArrayBackendResp) => {
          let standList = [];
          if (response.results) {
            standList = response.results.map(s => this.constructStand(s));
          }
          return {
            results: standList,
            error: response.error,
          };
        })
      );
  }

  constructStand(customStand: CustomStand): Stand {
    const properties = customStand.properties;

    const newCustomId = uuidv4().toLowerCase();
    customStand.id = newCustomId;

    let stand = new Stand({
      id: newCustomId,
      customId: newCustomId,
      parcelId: properties.parcel_id,
      parcelGid: properties.parcel_gid,
      area: properties.area,
      valuation: properties.valuation,
      pricingData: properties.financials,
      priceValuation: this.formatHelper.formatMinMaxValuation(
        properties.financials?.annual_valuation?.map(obj => obj.valuation)
      ),
      feature: customStand,
      svg: this.getGeomSVG(customStand),
    });
    delete stand.feature.properties.financials;
    return stand;
  }

  getGeomSVG(feature: any) {
    const boundingBox = turf.bbox(feature.geometry);
    const boundingBoxSquare = turf.square(boundingBox);
    return geojson2svg()
      .styles({
        MultiPolygon: { fill: '#F5BD02' },
        Polygon: { fill: '#F5BD02' },
      })
      .extent(boundingBoxSquare)
      .data(feature)
      .render();
  }

  cutAreaFromSelectedStands(
    geometry: any,
    siteId: number,
    currentGeom: any
  ): Observable<CustomAreaValuationStandArrayServiceResp> {
    let paramStand = [];
    paramStand.push({
      feature: JSON.stringify(currentGeom),
    });
    const body: CustomAreaValuationStandReq = {
      geometry: JSON.stringify(geometry),
      site_id: siteId,
      current_stands: paramStand,
    };

    return this.http
      .post<CustomAreaValuationStandArrayBackendResp>(`${this.apiUrl}cut_area_from_selected_stands`, body)
      .pipe(
        map((response: CustomAreaValuationStandArrayBackendResp) => {
          let standList = [];
          if (response.results) {
            standList = response.results.map(s => this.constructStand(s));
          }
          return {
            results: standList,
            error: response.error,
          };
        })
      );
  }

  selectAllStands(siteId: number): Observable<CustomAreaValuationStandArrayServiceResp> {
    this.spinnerService.show('app-spinner', 'Please Wait While All Of Your Property Is Calculated');
    return this.http.get<CustomAreaValuationStandArrayBackendResp>(`${this.apiUrl}select_all_stands/${siteId}`).pipe(
      map((response: CustomAreaValuationStandArrayBackendResp) => {
        let standList = [];
        if (response.results) {
          standList = response.results.map(s => this.constructStand(s));
        }
        this.spinnerService.hide('app-spinner');
        return {
          results: standList,
          error: response.error,
        };
      }),
      catchError(error => {
        this.spinnerService.hide('app-spinner');
        return of(error);
      })
    );
  }

  removePublicOverlap(site_id: number): Observable<Stand[]> {
    return this.http.post<any>(`${this.apiUrl}remove_public_overlap/${site_id}`, {}).pipe(
      map((response: CustomAreaValuationStandArrayBackendResp) => {
        let standsList = [];
        if (response.results) {
          standsList = response.results.map(s => this.constructStand(s));
        }
        return standsList;
      })
    );
  }

  getRedflagOverlapDetails(site_id: number): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}get_public_overlap/${site_id}`, {});
  }

  getParcelsByLatLng(lat: number = null, lng: number = null): Observable<Parcel[]> {
    let params = new HttpParams();

    if (lat && lng) {
      params = params.append('lat', lat.toString());
      params = params.append('lng', lng.toString());
    }

    return this.http.get<any>(`${this.apiUrl}get_parcel_by_lat_lng`, { params }).pipe(
      map(parcel => deserialize(parcel, Parcel)),
      catchError(error => {
        return of(error);
      })
    );
  }

  getFullPotentialValuation(siteId: number) {
    return this.http.get<any>(`${this.apiUrl}get_potential_valuation/${siteId}`).pipe(
      map((response: any) => {
        return response;
      })
    );
  }

  updateRegridDates() {
    return this.http.post<any>(`${this.apiUrl}update_regrid_refresh`, {});
  }

  queueRegridDownload(gid: number) {
    return this.http.post<any>(`${this.apiUrl}queue_download/${gid}`, {});
  }

  beginCaching(numProcesses: number = 5) {
    return this.http.post<any>(`${this.apiUrl}begin_caching/${numProcesses}`, {});
  }

  updateCountyData(gid: number, available: boolean, averageErt: string) {
    const body = {
      gid: gid,
      available: available,
      average_ert: averageErt,
    };
    return this.http.patch<any>(`${this.apiUrl}update_county_data`, body).pipe(
      map((response: any) => {
        return response.results;
      })
    );

  }

  getNewStands(geometry: any, siteId: number) {
    const body = {
      geometry: JSON.stringify(geometry),
    };

    return this.http
      .post<CustomAreaValuationStandArrayBackendResp>(`${this.apiUrl}removal_ert/${siteId}`, body)
      .pipe(
        map((response: CustomAreaValuationStandArrayBackendResp) => {
          let standList = [];
          if (response.results) {
            standList = response.results.map(s => this.constructStand(s));
          }
          return {
            results: standList,
            error: response.error,
          };
        })
      );
  }
}
